import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Common } from "@src/styles/Common";
import { Checkbox, DropDown, Input, Button } from "@src/components";
import React, { useState } from "react";
import SupportStatusModal from "./SupportStatusModal";
import { matchStatusType, useGetMatchProgress } from "@src/hooks/queries/useAgency";
import { useNavigate } from "react-router-dom";

const renderServiceType = (type?: number) => {
    switch (type) {
        case 1:
            return "경매대행";
        case 2:
            return "매수신청대리";
        case 3:
            return "매물컨설팅";
        default:
            return "-";
    }
};

const renderMatchStatus = (status?: matchStatusType) => {
    switch (status) {
        case "wait_experts":
            return "전문가 지원 대기 중";
        case "in_consuting":
            return "매물컨설팅 진행 중";
        case "complete_consuting":
            return "매물컨설팅 완료";
        case "before_auction_agent":
            return "매수신청대리 시작 대기중";
        case "in_auction_agent":
            return "매수신청대리 진행 중";
        case "complete_auction_agent":
            return "매수신청대리 완료";
        case "wait_settlement":
            return "정산 대기";
        case "complete_settlement":
            return "정산 완료";
        case "closed":
            return "정산 완료";
        default:
            return "-";
    }
};

const ExpertAgencyManage = () => {
    const navigate = useNavigate();

    const [serviceType, setServiceType] = useState<Record<string, boolean>>({
        경매대행: false,
        매물컨설팅: false,
        매수신청대리: false,
    });
    const [expertProgress, setExpertProgress] = useState<Record<string, boolean>>({
        "매물컨설팅 진행 중": false,
        "매수신청대리 진행 중": false,
    });
    const [clientProgress, setClientProgress] = useState<Record<string, boolean>>({
        모집중: false,
        진행중: false,
        완료: false,
        마감: false,
    });
    const [filters, setFilters] = useState<any>({});
    const [queryParams, setQueryParams] = useState<any>({});
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [page, setPage] = useState(1);
    const limit = 10;

    const { data } = useGetMatchProgress({ page, limit, ...queryParams }, true);

    const matchProgressList = data?.matches || [];
    const totalCount = data?.totalCount || 0;
    const monthlyCount = data?.monthlyCount || 0;

    const [selectedModalInfo, setSelectedModalInfo] = useState<{
        matchNo: number | null;
        takerType: "request" | "view" | "push" | null;
        count: number;
        serviceName: string;
        clientName: string;
    }>({ matchNo: null, takerType: null, count: 0, serviceName: "", clientName: "" });

    const openModal = (
        matchNo: number,
        takerType: "request" | "view" | "push",
        count: number,
        serviceName: string,
        clientName: string,
    ) => {
        setSelectedModalInfo({ matchNo, takerType, count, serviceName, clientName });
        setIsModalOpen(true);
    };

    const handleServiceTypeChange = (name: string, checked: boolean) => {
        setServiceType((prev) => ({ ...prev, [name]: checked }));
    };

    const handleExpertProgressChange = (name: string, checked: boolean) => {
        setExpertProgress((prev) => ({ ...prev, [name]: checked }));
    };

    const handleSearch = () => {
        const selectedServiceTypes = Object.entries(serviceType)
            .filter(([_, checked]) => checked)
            .map(([key]) => {
                if (key === "경매대행") return 1;
                if (key === "매수신청대리") return 2;
                if (key === "매물컨설팅") return 3;
                return 0;
            })
            .filter((v) => v !== 0);

        const selectedExpertProgress = Object.entries(expertProgress)
            .filter(([_, checked]) => checked)
            .map(([key]) => {
                if (key === "매물컨설팅 진행 중") return "in_consuting";
                if (key === "매수신청대리 진행 중") return "in_auction_agent";
                return "";
            })
            .filter((v) => v !== "");

        setQueryParams({
            serviceType: selectedServiceTypes.length > 0 ? selectedServiceTypes.join(",") : undefined,
            matchStatus: selectedExpertProgress.length > 0 ? selectedExpertProgress.join(",") : undefined,
            searchType: filters.searchValue ? filters.searchType : undefined,
            searchValue: filters.searchValue || undefined,
        });
        setPage(1);
    };

    const handleReset = () => {
        setServiceType({
            경매대행: false,
            매물컨설팅: false,
            매수신청대리: false,
        });
        setExpertProgress({
            "매물컨설팅 진행 중": false,
            "매수신청대리 진행 중": false,
        });
        setFilters({});
        setQueryParams({});
        setPage(1);
    };

    const dropList: {
        id: number;
        key: string;
        value: string;
    }[] = [
        { id: 0, key: "userName", value: "이름" },
        { id: 1, key: "userPhone", value: "휴대폰번호" },
        { id: 2, key: "officeName", value: "사무소명" },
        { id: 3, key: "itemSubId", value: "사건번호" },
        { id: 4, key: "itemAddress", value: "소재지" },
        { id: 5, key: "bannerNo", value: "광고번호" },
        { id: 6, key: "managerName", value: "담당 관리자" },
    ];

    return (
        <Layout>
            <div className="title">[전문가 매칭] 진행단계 확인</div>

            {/* 필터박스 */}
            <div>
                <div css={filterWrap}>
                    <ul>
                        <li>
                            <span css={subTitle}>서비스 종류</span>
                            <div css={checkBoxWrap}>
                                {["경매대행", "매물컨설팅", "매수신청대리"].map((check) => (
                                    <Checkbox
                                        key={check}
                                        isDisabled={false}
                                        isChecked={serviceType[check]}
                                        label={check}
                                        defaultValue={check}
                                        handleChange={(name, checked) =>
                                            handleServiceTypeChange(name as string, checked)
                                        }
                                    />
                                ))}
                            </div>
                        </li>
                        <li>
                            <span css={subTitle}>진행단계</span>
                            <div css={checkBoxWrap}>
                                {[
                                    "매물컨설팅 진행 중",
                                    "매수신청대리 진행 중",
                                ].map((check) => (
                                    <Checkbox
                                        key={check}
                                        isDisabled={false}
                                        isChecked={expertProgress[check]}
                                        label={check}
                                        defaultValue={check}
                                        handleChange={(name, checked) =>
                                            handleExpertProgressChange(name as string, checked)
                                        }
                                    />
                                ))}
                            </div>
                        </li>
                    </ul>
                </div>

                <div css={filterBtnBox}>
                    <Button color="gray" handleClick={handleReset} label="전체 해제" size="medium" />
                    <Button color="purple" handleClick={handleSearch} label="검색" size="medium" isDisabled={false} />
                </div>
            </div>

            {/* 테이블 */}
            <div css={tableWrap}>
                <div className="table-header">
                    누적 <span className="highlight">{totalCount.toLocaleString()}</span>건{" "}
                    <span className="divider">|</span> 이번 달{" "}
                    <span className="highlight">{monthlyCount.toLocaleString()}</span>건
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>번호</th>
                            <th>서비스</th>
                            <th>의뢰인 정보</th>
                            <th>전문가정보</th>
                            <th>지원자</th>
                            <th>의뢰 열람 전문가</th>
                            <th>알림 수신 전문가</th>
                            <th>진행단계</th>
                            <th>시작일</th>
                            <th>완료일</th>
                        </tr>
                    </thead>
                    <tbody>
                        {matchProgressList.map((match, index) => (
                            <tr key={index}>
                                <td>{(page - 1) * limit + index + 1}</td>
                                <td>{renderServiceType(match.serviceType)}</td>
                                <td>
                                    <span
                                        className="link-text"
                                        onClick={() => navigate(`/users/update/${match.matchUserNo}`)}
                                    >
                                        {match.matchUserName}
                                    </span>
                                </td>
                                <td>
                                    {match.takerUserName && match.takerUserName !== "-" ? (
                                        <span
                                            className="link-text"
                                            onClick={() => navigate(`/users/update/${match.matchTakerUserNo}`)}
                                        >
                                            {match.takerUserName}
                                        </span>
                                    ) : (
                                        "-"
                                    )}
                                </td>
                                <td>
                                    {match.requestedCount > 0 ? (
                                        <a
                                            href="#"
                                            className="link-text"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                openModal(
                                                    match.matchNo,
                                                    "request",
                                                    match.requestedCount,
                                                    renderServiceType(match.serviceType),
                                                    match.matchUserName,
                                                );
                                            }}
                                        >
                                            {match.requestedCount}
                                        </a>
                                    ) : (
                                        <strong>{match.requestedCount}</strong>
                                    )}
                                </td>
                                <td>
                                    {match.openMatchInfoCount > 0 ? (
                                        <a
                                            href="#"
                                            className="link-text"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                openModal(
                                                    match.matchNo,
                                                    "view",
                                                    match.openMatchInfoCount,
                                                    renderServiceType(match.serviceType),
                                                    match.matchUserName,
                                                );
                                            }}
                                        >
                                            {match.openMatchInfoCount}
                                        </a>
                                    ) : (
                                        <strong>{match.openMatchInfoCount}</strong>
                                    )}
                                </td>
                                <td>
                                    {match.alarmReceivedCount > 0 ? (
                                        <a
                                            href="#"
                                            className="link-text"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                openModal(
                                                    match.matchNo,
                                                    "push",
                                                    match.alarmReceivedCount,
                                                    renderServiceType(match.serviceType),
                                                    match.matchUserName,
                                                );
                                            }}
                                        >
                                            {match.alarmReceivedCount}
                                        </a>
                                    ) : (
                                        <strong>{match.alarmReceivedCount}</strong>
                                    )}
                                </td>
                                <td>
                                    <span className="outline-pill">{renderMatchStatus(match.matchStatus)}</span>
                                </td>
                                <td>{match.startDate ? match.startDate.split("T")[0].replace(/-/g, ".") : "-"}</td>
                                <td>{match.endDate ? match.endDate.split("T")[0].replace(/-/g, ".") : "-"}</td>
                            </tr>
                        ))}
                        {matchProgressList.length === 0 && (
                            <tr>
                                <td colSpan={10}>진행 중인 매칭이 없습니다.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <SupportStatusModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                matchNo={selectedModalInfo.matchNo}
                takerType={selectedModalInfo.takerType}
                count={selectedModalInfo.count}
                serviceName={selectedModalInfo.serviceName}
                clientName={selectedModalInfo.clientName}
            />
        </Layout>
    );
};

const Layout = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 40px;

    .title {
        color: #000;
        font-size: 16px;
        font-weight: 400;
        line-height: 22.4px;
    }
`;

const filterWrap = css`
    margin-top: 20px;
    width: 100%;
    padding: 16px 24px;
    border-radius: 8px;
    background-color: ${Common.colors.gray100};
    li {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        margin-bottom: 12px;
        &:last-of-type {
            margin-bottom: 0;
        }
        > div {
            display: flex;
            justify-content: flex-start;
            align-items: center;
        }
    }
    select {
        border-width: 1px;
    }
    input[name="searchInput"] {
        margin-left: 12px;
        height: 36px;
        border: 1px solid ${Common.colors.gray200};
        border-radius: 8px;
        &::placeholder {
            color: ${Common.colors.gray500};
        }
    }
`;

const subTitle = css`
    ${Common.textStyle.title6_R14}
    color: ${Common.colors.black};
    display: inline-block;
    width: 100px;
`;

const checkBoxWrap = css`
    display: flex;
    & > div {
        margin-right: 16px;
    }
`;
const dateButton = (isActive: boolean) => css`
    padding: 10px;
    margin: 5px;
    background-color: white;
    border: 1px solid gray;
    cursor: pointer;
    border-radius: 8px;
    ${isActive && { fontWeight: 700 }}
`;

const filterBtnBox = css`
    display: flex;
    gap: 10px;
    flex-direction: row;
    justify-content: flex-end;
    padding-top: 10px;
`;

const cardWrap = css`
    display: flex;
    gap: 20px;
    width: 100%;

    .card {
        flex: 1;
        background: #ffffff;
        border: 1px solid ${Common.colors.gray200};
        border-radius: 8px;
        padding: 24px;

        .card-title {
            font-size: 16px;
            font-weight: 500;
            color: #374151;
            margin-bottom: 24px;
        }

        .card-content {
            display: flex;
            gap: 40px;

            > div {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            .label {
                font-size: 13px;
                color: ${Common.colors.gray500};
            }

            .value {
                font-size: 28px;
                font-weight: 700;
                color: ${Common.colors.black};
                display: flex;
                align-items: baseline;

                .unit {
                    font-size: 14px;
                    font-weight: 400;
                    color: ${Common.colors.gray500};
                    margin-left: 4px;
                }

                &.active {
                    color: #1a56ff;
                }
            }
        }
    }
`;

const tableWrap = css`
    width: 100%;
    background: #ffffff;
    border: 1px solid ${Common.colors.gray200};
    border-radius: 8px;
    overflow: hidden;

    .table-header {
        padding: 20px 24px;
        font-size: 14px;
        color: ${Common.colors.gray500};
        border-bottom: 1px solid ${Common.colors.gray200};
        text-align: left;

        .highlight {
            color: ${Common.colors.black};
            font-weight: 700;
        }

        .divider {
            margin: 0 12px;
            color: ${Common.colors.gray300};
        }
    }

    table {
        width: 100%;
        border-collapse: collapse;
        text-align: center;

        th,
        td {
            padding: 16px 12px;
            font-size: 14px;
            color: ${Common.colors.black};
            border-bottom: 1px solid ${Common.colors.gray200};
            vertical-align: middle;
        }

        th {
            color: ${Common.colors.gray500};
            font-weight: 500;
            background-color: #ffffff;
        }

        tr:last-child td {
            border-bottom: none;
        }

        .link-text {
            color: ${Common.colors.black};
            font-weight: 600;
            text-decoration: underline;
            text-underline-offset: 4px;
            cursor: pointer;
        }

        .outline-pill {
            display: inline-block;
            padding: 6px 14px;
            border-radius: 4px;
            border: 1px solid ${Common.colors.gray300};
            color: ${Common.colors.gray600};
            font-size: 13px;
            font-weight: 500;
            background-color: #ffffff;
        }
    }
`;

export default ExpertAgencyManage;
